import React, { useState, useEffect, useCallback, useRef } from 'react';
import jobsMonitorService from '../services/jobsMonitorService.js';
import certusService from '../../certus/services/certusService.js';
import { useAuth } from '../../../contexts/AuthContext';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    counts: {
      pending: 0,
      inProgress: 0,
      completed: 0,
      failed: 0,
      total: 0
    },
    activeWorkers: 0,
    workerIds: [],
    jobsByType: {}
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState('status'); // Default sort by status (pending/in-progress first)
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' or 'desc'
  const [purging, setPurging] = useState(false);
  const [processingJobs, setProcessingJobs] = useState(false);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [submitTab, setSubmitTab] = useState('pdf'); // 'pdf', 'linkedin', or 'bulk'
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  // Bulk submit state
  const [bulkPdfCount, setBulkPdfCount] = useState(5);
  const [bulkLinkedinCount, setBulkLinkedinCount] = useState(5);
  // LinkedIn form state
  const [linkedinUsername, setLinkedinUsername] = useState('vranjesevic@gmail.com');
  const [linkedinPassword, setLinkedinPassword] = useState('1#plan3plan3');
  const [linkedinSearchUrl, setLinkedinSearchUrl] = useState('https://www.linkedin.com/search/results/people/?keywords=california%20startup&origin=SWITCH_SEARCH_VERTICAL&sid=-G7');
  const [maxPages, setMaxPages] = useState(3);
  const [maxProfiles, setMaxProfiles] = useState(100);
  // PDF form state
  const [pdfFile, setPdfFile] = useState(null);
  const [downloading, setDownloading] = useState({}); // Track downloading state by jobId
  // System pause state
  const [systemPaused, setSystemPaused] = useState(false);
  const [pauseReason, setPauseReason] = useState('');
  const [togglingPause, setTogglingPause] = useState(false);
  const [resourceMetrics, setResourceMetrics] = useState(null);
  const [deploymentInfo, setDeploymentInfo] = useState(null);
  const [showDeploymentInfo, setShowDeploymentInfo] = useState(false);
  const isRefreshingRef = useRef(false); // Use ref for synchronous updates
  const { user} = useAuth();

  const loadSystemStatus = useCallback(async () => {
    try {
      const statusData = await jobsMonitorService.getSystemStatus();
      setSystemPaused(statusData.paused || false);
      setPauseReason(statusData.reason || '');
    } catch (err) {
      console.error('Error loading system status:', err);
      // Don't show error to user for system status failures
    }
  }, []);

  const loadResourceMetrics = useCallback(async (forceRefresh = false) => {
    try {
      const metricsData = await jobsMonitorService.getResourceMetrics(forceRefresh);
      setResourceMetrics(metricsData);
    } catch (err) {
      console.error('Error loading resource metrics:', err);
      // Silent fail - resource metrics are nice-to-have
    }
  }, []);

  const loadDeploymentInfo = useCallback(async () => {
    try {
      const info = await jobsMonitorService.getDeploymentInfo();
      setDeploymentInfo(info);
    } catch (err) {
      console.error('Error loading deployment info:', err);
      // Silent fail - deployment info is informational
    }
  }, []);

  const loadDashboardData = useCallback(async (showLoading = true) => {
    // Prevent overlapping requests (important for slow stats queries with many jobs)
    if (isRefreshingRef.current) {
      console.log('⏭️  Skipping refresh - previous request still in progress');
      return;
    }
    
    isRefreshingRef.current = true;  // Set flag synchronously (ref updates immediately)
    
    // Safety: Force reset flag after 90 seconds (in case of catastrophic failure)
    const safetyTimeout = setTimeout(() => {
      console.warn('⚠️  Force-resetting isRefreshing flag after 90s timeout');
      isRefreshingRef.current = false;
    }, 90000);
    
    try {
      if (showLoading) {
        setLoading(true);
        setError(null);
      }
      const [statsData, recentData, statusData] = await Promise.all([
        jobsMonitorService.getStats(),
        jobsMonitorService.getRecentJobs(100), // Reduced from 500 to improve performance
        jobsMonitorService.getSystemStatus().catch(() => ({ paused: false, reason: '' }))
      ]);
      // Defensive checks: ensure data is valid
      if (!statsData || typeof statsData !== 'object') {
        console.error('Invalid stats data:', statsData);
        setStats({
          counts: { pending: 0, inProgress: 0, completed: 0, failed: 0, total: 0 },
          activeWorkers: 0,
          workerIds: [],
          jobsByType: {}
        });
      } else {
        setStats(statsData);
      }
      
      if (!Array.isArray(recentData)) {
        console.error('Invalid recent jobs data:', recentData);
        setRecentJobs([]);
      } else {
        setRecentJobs(recentData);
      }
      
      // Update system pause status
      setSystemPaused(statusData.paused || false);
      setPauseReason(statusData.reason || '');
      
      // Process completed terminix_scrape jobs in the background (silent)
      // Check if there are any completed terminix_scrape jobs
      const completedCompetitorJobs = recentData.filter(job => 
        job.status === 'completed' && job.jobType === 'terminix_scrape'
      );
      
      if (completedCompetitorJobs.length > 0) {
        // Process jobs silently in background (don't await - fire and forget)
        certusService.processCompletedCompetitorJobs(50).catch(err => {
          // Silent fail - just log, don't show error to user
          console.log('Background job processing (silent):', err.message);
        });
      }
      
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      if (showLoading) {
        // Provide more detailed error messages
        let errorMessage = 'Failed to load jobs data. Please try again.';
        if (err.response) {
          // Server responded with error status
          errorMessage = err.response.data?.error || `Server error: ${err.response.status}`;
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Could not reach the server. Please check if the backend is running.';
        } else {
          // Something else happened
          errorMessage = err.message || errorMessage;
        }
        setError(errorMessage);
      }
    } finally {
      clearTimeout(safetyTimeout); // Cancel safety timeout
      isRefreshingRef.current = false; // Reset flag synchronously
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 30 seconds if enabled (increased for large datasets)
    let interval;
    if (autoRefresh) {
      interval = setInterval(() => {
        loadDashboardData(false); // Silent refresh
      }, 30000); // 30 seconds - allows time for slow queries with many jobs
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, loadDashboardData]);

  // Load AWS resource metrics on mount and refresh every 30 minutes
  useEffect(() => {
    loadResourceMetrics();
    loadDeploymentInfo(); // Load deployment info once on mount
    
    const metricsInterval = setInterval(() => {
      loadResourceMetrics(false); // Use cache if available, refresh if expired
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(metricsInterval);
  }, [loadResourceMetrics, loadDeploymentInfo]);

  const formatTime = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleString();
    } catch {
      return date.toString();
    }
  };

  // Sort jobs by column
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted jobs
  const getSortedJobs = () => {
    // Defensive check: ensure recentJobs is an array
    if (!Array.isArray(recentJobs)) {
      console.warn('recentJobs is not an array:', recentJobs);
      return [];
    }
    const sorted = [...recentJobs];
    sorted.sort((a, b) => {
      let aValue, bValue;
      let comparison = 0;
      
      switch (sortColumn) {
        case 'jobId':
          aValue = a.jobId || '';
          bValue = b.jobId || '';
          break;
        case 'type':
          aValue = a.jobType || '';
          bValue = b.jobType || '';
          break;
        case 'status':
          // Use priority: pending=3, in_progress=2, completed=1
          const statusPriority = { 'pending': 3, 'in_progress': 2, 'completed': 1, 'failed': 0 };
          aValue = statusPriority[a.status] ?? -1;
          bValue = statusPriority[b.status] ?? -1;
          // For status, compare as numbers
          comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          return sortDirection === 'asc' ? comparison : -comparison;
        case 'worker':
          aValue = a.workerId || '';
          bValue = b.workerId || '';
          break;
        case 'created':
          aValue = a.createdAt || '';
          bValue = b.createdAt || '';
          break;
        case 'completed':
          aValue = a.completedAt || a.createdAt || '';
          bValue = b.completedAt || b.createdAt || '';
          break;
        default:
          return 0;
      }
      
      // Handle null/empty values
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      // Compare values
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        comparison = aValue.localeCompare(bValue);
      } else {
        comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  };

  // Render sort indicator
  const getSortIndicator = (column) => {
    if (sortColumn !== column) {
      return <span className="sort-indicator">↕️</span>;
    }
    return sortDirection === 'asc' ? (
      <span className="sort-indicator">↑</span>
    ) : (
      <span className="sort-indicator">↓</span>
    );
  };

  // Handle PDF file selection
  const handlePdfFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        alert('Please select a PDF file');
        e.target.value = '';
        return;
      }
      setPdfFile(file);
    }
  };

  // Submit PDF job
  const handleSubmitPdf = async () => {
    if (!pdfFile) {
      alert('Please select a PDF file');
      return;
    }

    setSubmitting(true);
    setSubmitSuccess(null);
    setError(null);

    try {
      const result = await jobsMonitorService.submitPdfJob(pdfFile);
      setSubmitSuccess(`✅ PDF job submitted successfully! Job ID: ${result.jobId}`);
      setPdfFile(null);
      // Reset file input
      const fileInput = document.getElementById('pdf-file-input');
      if (fileInput) fileInput.value = '';
      
      // Refresh dashboard after a short delay
      setTimeout(() => {
        loadDashboardData();
      }, 1000);
    } catch (err) {
      console.error('PDF submission error:', err);
      const errorMessage = err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to submit PDF job';
      const errorCode = err.response?.data?.code || '';
      setError(errorCode ? `${errorMessage} (${errorCode})` : errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Submit LinkedIn job
  const handleSubmitLinkedIn = async () => {
    if (!linkedinUsername || !linkedinPassword || !linkedinSearchUrl) {
      alert('Please fill in all required fields');
      return;
    }

    if (!linkedinSearchUrl.startsWith('https://www.linkedin.com/search/results/people/')) {
      alert('Please enter a valid LinkedIn people search URL');
      return;
    }

    setSubmitting(true);
    setSubmitSuccess(null);
    setError(null);

    try {
      const result = await jobsMonitorService.submitLinkedInJob(
        linkedinUsername,
        linkedinPassword,
        linkedinSearchUrl,
        parseInt(maxPages) || 3,
        parseInt(maxProfiles) || 100
      );
      setSubmitSuccess(`✅ LinkedIn job submitted successfully! Job ID: ${result.jobId}`);
      // Clear form
      setLinkedinUsername('');
      setLinkedinPassword('');
      setLinkedinSearchUrl('');
      setMaxPages(3);
      setMaxProfiles(100);
      
      // Refresh dashboard after a short delay
      setTimeout(() => {
        loadDashboardData();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to submit LinkedIn job');
    } finally {
      setSubmitting(false);
    }
  };

  // Submit bulk jobs for stress testing
  const handleBulkSubmit = async () => {
    const totalJobs = bulkPdfCount + bulkLinkedinCount;
    
    if (totalJobs === 0) {
      alert('Please specify at least one job to submit');
      return;
    }

    if (!window.confirm(`Submit ${totalJobs} jobs?\n\n- ${bulkPdfCount} PDF jobs\n- ${bulkLinkedinCount} LinkedIn jobs\n\nThis will submit all jobs in ONE batch (realistic stress test).`)) {
      return;
    }

    setSubmitting(true);
    setSubmitSuccess(null);
    setError(null);

    try {
      setSubmitSuccess(`📤 Submitting ${totalJobs} jobs in ONE batch...\nThis may take 30-60 seconds for large batches.`);
      
      // Submit ALL jobs in ONE Lambda call (realistic stress test)
      const results = await jobsMonitorService.submitBulkJobs(bulkPdfCount, bulkLinkedinCount);
      
      setSubmitSuccess(`✅ Successfully submitted ${results.totalSubmitted} jobs!\n- ${results.pdfSubmitted} PDF jobs\n- ${results.linkedinSubmitted} LinkedIn jobs\n\nJobs are being processed by workers...`);
      
      // Refresh dashboard to show new jobs
      await loadDashboardData();
    } catch (err) {
      console.error('Bulk submission error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to submit bulk jobs');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle processing completed terminix jobs
  const handleProcessJobs = async () => {
    setProcessingJobs(true);
    try {
      const result = await certusService.processCompletedCompetitorJobs(50);
      alert(`✅ Processed ${result.processed || 0} jobs successfully!\n\nErrors: ${result.errors || 0}`);
      // Reload dashboard data to show updated job statuses
      await loadDashboardData(false);
    } catch (err) {
      console.error('Error processing jobs:', err);
      alert(`❌ Failed to process jobs: ${err.response?.data?.error || err.message || 'Unknown error'}`);
    } finally {
      setProcessingJobs(false);
    }
  };

  // Handle purge all jobs
  const handlePurgeAll = async () => {
    const totalJobs = stats.counts?.total ?? 0;
    const confirmMessage = `⚠️ DANGER: This will permanently delete ALL ${totalJobs} jobs from the queue!\n\nThis action cannot be undone.\n\nType "DELETE ALL" to confirm:`;
    
    const userInput = window.prompt(confirmMessage);
    if (userInput !== 'DELETE ALL') {
      return; // User cancelled or typed wrong
    }

    // Double confirmation
    const doubleConfirm = window.confirm(
      `Are you absolutely sure you want to delete all ${totalJobs} jobs?\n\nThis will remove:\n- ${stats.counts?.pending ?? 0} pending jobs\n- ${stats.counts?.inProgress ?? 0} in-progress jobs\n- ${stats.counts?.completed ?? 0} completed jobs\n\nThis cannot be undone!`
    );

    if (!doubleConfirm) {
      return;
    }

    setPurging(true);
    setError(null);
    
    try {
      const result = await jobsMonitorService.purgeAllJobs();
      alert(`✅ Successfully purged ${result.deletedCount} jobs from the queue!`);
      
      // Reload dashboard data
      await loadDashboardData();
    } catch (err) {
      console.error('Error purging jobs:', err);
      setError(err.response?.data?.error || err.message || 'Failed to purge jobs');
    } finally {
      setPurging(false);
    }
  };

  const handleTogglePause = async () => {
    const newPausedState = !systemPaused;
    let reason = '';
    
    if (newPausedState) {
      // Pausing system - ask for reason
      reason = window.prompt('Enter reason for pausing job submissions (optional):');
      if (reason === null) {
        return; // User cancelled
      }
    }
    
    setTogglingPause(true);
    try {
      await jobsMonitorService.setSystemPause(newPausedState, reason || '');
      setSystemPaused(newPausedState);
      setPauseReason(reason || '');
      
      const message = newPausedState 
        ? `🔒 Job submissions PAUSED${reason ? ': ' + reason : ''}` 
        : '✅ Job submissions RESUMED';
      alert(message);
    } catch (err) {
      console.error('Error toggling system pause:', err);
      alert(`❌ Failed to ${newPausedState ? 'pause' : 'resume'} system. See console for details.`);
    } finally {
      setTogglingPause(false);
    }
  };

  // Handle job download
  const handleDownload = async (jobId, jobType) => {
    if (downloading[jobId]) {
      return; // Already downloading
    }
    
    setDownloading(prev => ({ ...prev, [jobId]: true }));
    
    try {
      const result = await jobsMonitorService.getJobDownloadUrl(jobId);
      
      if (result.success && result.downloadUrl) {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = result.fileName || `job_${jobId}.${jobType === 'linkedin_people_search' ? 'csv' : 'txt'}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert('❌ Failed to get download URL');
      }
    } catch (err) {
      console.error('Error downloading job:', err);
      alert(`❌ Failed to download: ${err.response?.data?.error || err.message || 'Unknown error'}`);
    } finally {
      setDownloading(prev => {
        const newState = { ...prev };
        delete newState[jobId];
        return newState;
      });
    }
  };

  if (loading) {
    return (
      <div className="jobs-monitor-dashboard">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading jobs monitor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="jobs-monitor-dashboard">
      <div className="dashboard-header">
        <h1>📊 Jobs System Monitor</h1>
        <p>Monitor DynamoDB Jobs System operations in real-time</p>
        <div className="header-actions">
          <button 
            className={`btn ${autoRefresh ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? '⏸️ Pause Auto-Refresh' : '▶️ Resume Auto-Refresh'}
          </button>
          <button className="btn btn-secondary" onClick={() => loadDashboardData()}>
            🔄 Refresh Now
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowDeploymentInfo(!showDeploymentInfo)}
            title="Show deployment and hosting information"
          >
            {showDeploymentInfo ? '🔼 Hide Info' : '🌐 Deployment Info'}
          </button>
          {user?.role === 'admin' && (
            <button 
              className="btn btn-danger" 
              onClick={handlePurgeAll}
              disabled={purging || (stats.counts?.total ?? 0) === 0}
              title="Delete all jobs from the queue (Admin only)"
            >
              {purging ? '🗑️ Purging...' : `🗑️ Purge All Jobs (${stats.counts?.total ?? 0})`}
            </button>
          )}
          {user?.role === 'admin' && (
            <button 
              className={`btn ${systemPaused ? 'btn-success' : 'btn-warning'}`}
              onClick={handleTogglePause}
              disabled={togglingPause}
              title={systemPaused ? 'Resume job submissions' : 'Pause job submissions (useful for maintenance)'}
            >
              {togglingPause 
                ? '⏳ Updating...' 
                : (systemPaused ? '▶️ Resume Job Submissions' : '⏸️ Pause Job Submissions')}
            </button>
          )}
          <button 
            className="btn btn-info" 
            onClick={handleProcessJobs}
            disabled={processingJobs}
            title="Process completed Competitor scrape jobs and save pricing data"
          >
            {processingJobs ? '⏳ Processing...' : '🔄 Process Completed Jobs'}
          </button>
          <button 
            className="btn btn-primary" 
            onClick={() => setShowSubmitForm(!showSubmitForm)}
          >
            {showSubmitForm ? '➖ Hide Submit Form' : '➕ Submit New Job'}
          </button>
          {lastRefresh && (
            <span className="last-refresh">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="dashboard-content">
        {/* Deployment Info Section */}
        {showDeploymentInfo && deploymentInfo && (
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            border: '2px solid #007bff'
          }}>
            <h2 style={{ marginTop: 0, marginBottom: '16px', color: '#007bff' }}>
              🌐 Deployment & Hosting Information
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {/* Environment Info */}
              <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', background: '#f8f9fa' }}>
                <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#007bff' }}>
                  🏷️ Environment
                </div>
                <div style={{ fontSize: '13px', color: '#555', display: 'grid', gap: '6px' }}>
                  <div><strong>Environment:</strong> {deploymentInfo.environment}</div>
                  <div><strong>Mode:</strong> {deploymentInfo.mode}</div>
                  {deploymentInfo.deployment && (
                    <div><strong>Deployment:</strong> {deploymentInfo.deployment.name}</div>
                  )}
                </div>
              </div>
              
              {/* Frontend Hosting */}
              {deploymentInfo.frontend && (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', background: '#f8f9fa' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#007bff' }}>
                    🎨 Frontend
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', display: 'grid', gap: '6px' }}>
                    {deploymentInfo.frontend.server && (
                      <div><strong>Server:</strong> {deploymentInfo.frontend.server}</div>
                    )}
                    {deploymentInfo.frontend.hosting && (
                      <div><strong>Hosting:</strong> {deploymentInfo.frontend.hosting}</div>
                    )}
                    {deploymentInfo.frontend.url && (
                      <div><strong>URL:</strong> {deploymentInfo.frontend.url}</div>
                    )}
                    {deploymentInfo.frontend.hot && (
                      <div><strong>HMR:</strong> {deploymentInfo.frontend.hot}</div>
                    )}
                    {deploymentInfo.frontend.bucket && (
                      <div><strong>S3 Bucket:</strong> {deploymentInfo.frontend.bucket}</div>
                    )}
                    {deploymentInfo.frontend.customUrl && (
                      <div><strong>Custom URL:</strong> <a href={deploymentInfo.frontend.customUrl} target="_blank" rel="noopener noreferrer">{deploymentInfo.frontend.customUrl}</a></div>
                    )}
                    {deploymentInfo.deployment?.cloudFront && (
                      <>
                        <div><strong>CloudFront ID:</strong> {deploymentInfo.deployment.cloudFront.distributionId}</div>
                        <div><strong>CloudFront URL:</strong> <a href={deploymentInfo.deployment.cloudFront.url} target="_blank" rel="noopener noreferrer">{deploymentInfo.deployment.cloudFront.domain}</a></div>
                      </>
                    )}
                  </div>
                </div>
              )}
              
              {/* Backend Info */}
              {deploymentInfo.backend && (
                <div style={{ border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', background: '#f8f9fa' }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#007bff' }}>
                    ⚡ Backend
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', display: 'grid', gap: '6px' }}>
                    <div><strong>Type:</strong> {deploymentInfo.backend.type}</div>
                    {deploymentInfo.backend.url && (
                      <div><strong>URL:</strong> {deploymentInfo.backend.url}</div>
                    )}
                    {deploymentInfo.backend.runtime && (
                      <div><strong>Runtime:</strong> {deploymentInfo.backend.runtime}</div>
                    )}
                    {deploymentInfo.backend.platform && (
                      <div><strong>Platform:</strong> {deploymentInfo.backend.platform}</div>
                    )}
                    {deploymentInfo.backend.apiGateway && (
                      <>
                        <div><strong>API Gateway ID:</strong> {deploymentInfo.backend.apiGateway.id}</div>
                        <div><strong>Stage:</strong> {deploymentInfo.backend.apiGateway.stage}</div>
                        <div><strong>API URL:</strong> {deploymentInfo.backend.apiGateway.url}</div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Notes Section (for localhost) */}
            {deploymentInfo.notes && (
              <div style={{ marginTop: '16px', padding: '12px', background: '#e7f3ff', borderRadius: '6px', border: '1px solid #007bff' }}>
                <div style={{ fontSize: '14px', color: '#0056b3', marginBottom: '8px', fontWeight: '600' }}>
                  ℹ️ Notes
                </div>
                {deploymentInfo.notes.lambdaMetrics && (
                  <div style={{ fontSize: '13px', color: '#333', marginBottom: '4px' }}>
                    • {deploymentInfo.notes.lambdaMetrics}
                  </div>
                )}
                {deploymentInfo.notes.sharedResources && (
                  <div style={{ fontSize: '13px', color: '#333' }}>
                    • {deploymentInfo.notes.sharedResources}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Job Submission Form */}
        {showSubmitForm && (
          <div className="submit-job-section">
            <h2>📤 Submit New Job</h2>
            <div className="submit-tabs">
              <button 
                className={`submit-tab ${submitTab === 'pdf' ? 'active' : ''}`}
                onClick={() => setSubmitTab('pdf')}
              >
                📄 PDF to Text
              </button>
              <button 
                className={`submit-tab ${submitTab === 'linkedin' ? 'active' : ''}`}
                onClick={() => setSubmitTab('linkedin')}
              >
                🔍 LinkedIn Search
              </button>
              <button 
                className={`submit-tab ${submitTab === 'bulk' ? 'active' : ''}`}
                onClick={() => setSubmitTab('bulk')}
              >
                🚀 Bulk Submit (Stress Test)
              </button>
            </div>

            {submitTab === 'pdf' && (
              <div className="submit-form">
                <div className="form-group">
                  <label htmlFor="pdf-file-input">Select PDF File:</label>
                  <input
                    id="pdf-file-input"
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={handlePdfFileChange}
                    disabled={submitting}
                  />
                  {pdfFile && (
                    <div className="file-info">
                      📄 {pdfFile.name} ({(pdfFile.size / 1024).toFixed(2)} KB)
                    </div>
                  )}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmitPdf}
                  disabled={!pdfFile || submitting}
                >
                  {submitting ? '⏳ Submitting...' : '📤 Submit PDF Job'}
                </button>
              </div>
            )}

            {submitTab === 'linkedin' && (
              <div className="submit-form">
                <div className="form-group">
                  <label htmlFor="linkedin-username">LinkedIn Username (Email):</label>
                  <input
                    id="linkedin-username"
                    type="email"
                    value={linkedinUsername}
                    onChange={(e) => setLinkedinUsername(e.target.value)}
                    placeholder="your.email@example.com"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="linkedin-password">LinkedIn Password:</label>
                  <input
                    id="linkedin-password"
                    type="password"
                    value={linkedinPassword}
                    onChange={(e) => setLinkedinPassword(e.target.value)}
                    placeholder="Enter your LinkedIn password"
                    disabled={submitting}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="linkedin-search-url">LinkedIn Search URL:</label>
                  <input
                    id="linkedin-search-url"
                    type="url"
                    value={linkedinSearchUrl}
                    onChange={(e) => setLinkedinSearchUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/search/results/people/?..."
                    disabled={submitting}
                  />
                  <small>Copy the full LinkedIn people search URL from your browser</small>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="max-pages">Max Pages:</label>
                    <input
                      id="max-pages"
                      type="number"
                      min="1"
                      max="10"
                      value={maxPages}
                      onChange={(e) => setMaxPages(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="max-profiles">Max Profiles:</label>
                    <input
                      id="max-profiles"
                      type="number"
                      min="1"
                      max="500"
                      value={maxProfiles}
                      onChange={(e) => setMaxProfiles(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSubmitLinkedIn}
                  disabled={submitting || !linkedinUsername || !linkedinPassword || !linkedinSearchUrl}
                >
                  {submitting ? '⏳ Submitting...' : '📤 Submit LinkedIn Job'}
                </button>
              </div>
            )}

            {submitTab === 'bulk' && (
              <div className="submit-form">
                <p style={{marginBottom: '20px', color: '#666'}}>
                  🚀 <strong>Stress Test Mode</strong>: Submit multiple jobs at once to test worker scaling and performance.
                  <br/>
                  PDF files will be randomly selected from the TestingPDFs folder.
                  <br/>
                  LinkedIn jobs will use your pre-filled credentials and search URL.
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="bulk-pdf-count">Number of PDF Jobs:</label>
                    <input
                      id="bulk-pdf-count"
                      type="text"
                      value={bulkPdfCount}
                      onChange={(e) => setBulkPdfCount(parseInt(e.target.value) || 0)}
                      disabled={submitting}
                      placeholder="0-50"
                    />
                    <small>Random PDFs from TestingPDFs folder (0-50)</small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="bulk-linkedin-count">Number of LinkedIn Jobs:</label>
                    <input
                      id="bulk-linkedin-count"
                      type="text"
                      value={bulkLinkedinCount}
                      onChange={(e) => setBulkLinkedinCount(parseInt(e.target.value) || 0)}
                      disabled={submitting}
                      placeholder="0-50"
                    />
                    <small>Using pre-filled credentials and search URL (0-50)</small>
                  </div>
                </div>
                <div style={{marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', fontSize: '14px'}}>
                  <strong>Total Jobs: {bulkPdfCount + bulkLinkedinCount}</strong>
                  <br/>
                  This will test the system's ability to handle multiple workers processing jobs concurrently.
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleBulkSubmit}
                  disabled={submitting || (bulkPdfCount + bulkLinkedinCount) === 0}
                  style={{marginTop: '16px'}}
                >
                  {submitting ? '⏳ Submitting Bulk Jobs...' : `🚀 Submit ${bulkPdfCount + bulkLinkedinCount} Jobs`}
                </button>
              </div>
            )}

            {submitSuccess && (
              <div className="submit-success" style={{
                background: '#d4edda',
                color: '#155724',
                padding: '12px 16px',
                borderRadius: '6px',
                marginTop: '16px',
                border: '1px solid #c3e6cb'
              }}>
                {submitSuccess}
              </div>
            )}
          </div>
        )}

        {systemPaused && (
          <div className="pause-banner" style={{
            background: '#fff3cd',
            color: '#856404',
            padding: '16px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '2px solid #ffc107',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '16px',
            fontWeight: '600'
          }}>
            <span style={{ fontSize: '24px' }}>⏸️</span>
            <div>
              <div>🔒 JOB SUBMISSIONS ARE PAUSED</div>
              {pauseReason && (
                <div style={{ fontSize: '14px', fontWeight: 'normal', marginTop: '4px' }}>
                  Reason: {pauseReason}
                </div>
              )}
            </div>
          </div>
        )}

        {error && (
          <div className="error-message" style={{
            background: '#f8d7da',
            color: '#721c24',
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '20px',
            border: '1px solid #f5c6cb'
          }}>
            ⚠️ {error}
          </div>
        )}
        {/* Statistics Cards */}
        <div className="stats-grid">
          <div className="stat-card pending">
            <div className="stat-icon">⏳</div>
            <div className="stat-content">
              <div className="stat-label">Pending Jobs</div>
              <div className="stat-value">{stats.counts?.pending ?? 0}</div>
            </div>
          </div>
          <div className="stat-card in-progress">
            <div className="stat-icon">🔄</div>
            <div className="stat-content">
              <div className="stat-label">In Progress</div>
              <div className="stat-value">{stats.counts?.inProgress ?? 0}</div>
            </div>
          </div>
          <div className="stat-card completed">
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <div className="stat-label">Completed</div>
              <div className="stat-value">{stats.counts?.completed ?? 0}</div>
            </div>
          </div>
          <div className="stat-card failed">
            <div className="stat-icon">❌</div>
            <div className="stat-content">
              <div className="stat-label">Failed</div>
              <div className="stat-value">{stats.counts?.failed ?? 0}</div>
            </div>
          </div>
          <div className="stat-card total">
            <div className="stat-icon">📦</div>
            <div className="stat-content">
              <div className="stat-label">Total Jobs</div>
              <div className="stat-value">{stats.counts?.total ?? 0}</div>
            </div>
          </div>
          <div className="stat-card workers">
            <div className="stat-icon">👷</div>
            <div className="stat-content">
              <div className="stat-label">Active Workers</div>
              <div className="stat-value">{stats.activeWorkers ?? 0}</div>
            </div>
          </div>
        </div>

        {/* Job Type Breakdown */}
        {stats.jobsByType && Object.keys(stats.jobsByType).length > 0 && (
          <div className="jobs-by-type">
            <h2>Jobs by Type</h2>
            <div className="type-list">
              {stats.jobsByType && Object.entries(stats.jobsByType).map(([type, count]) => (
                <div key={type} className="type-item">
                  <span className="type-name">{type}</span>
                  <span className="type-count">{count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Active Machines */}
        {stats.activeMachines && Array.isArray(stats.activeMachines) && stats.activeMachines.length > 0 && (
          <div className="active-machines" style={{
            background: 'white',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <h2>🖥️ Active Machines ({stats.activeMachines.length})</h2>
            <div style={{ display: 'grid', gap: '12px', marginTop: '16px' }}>
              {stats.activeMachines.map((machine) => (
                <div key={machine.machineId} style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '12px 16px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '600', fontSize: '14px', color: '#333' }}>
                      Machine: {machine.machineId}
                    </span>
                    <span style={{ 
                      background: '#007bff', 
                      color: 'white', 
                      padding: '2px 8px', 
                      borderRadius: '12px', 
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {machine.total} workers
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', color: '#666' }}>
                    🖥️ {machine.cli} CLI  •  🎨 {machine.gui} GUI
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* AWS Resource Metrics */}
        {resourceMetrics && !resourceMetrics.error && (
          <div className="aws-resources" style={{
            background: 'white',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2>☁️ AWS Resource Usage</h2>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {resourceMetrics.timestamp ? 
                  `Last updated: ${new Date(resourceMetrics.timestamp).toLocaleTimeString()}` : 
                  'Loading...'
                }
                <button 
                  onClick={() => loadResourceMetrics(true)}
                  style={{
                    marginLeft: '12px',
                    padding: '4px 8px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    background: 'white'
                  }}
                >
                  🔄 Refresh
                </button>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {/* DynamoDB Card */}
              {resourceMetrics.dynamodb && !resourceMetrics.dynamodb.error && (
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '16px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#FF9900' }}>
                    🗄️ DynamoDB
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', display: 'grid', gap: '6px' }}>
                    {resourceMetrics.dynamodb.tableName && (
                      <div><strong>Table:</strong> {resourceMetrics.dynamodb.tableName}</div>
                    )}
                    <div><strong>Items:</strong> {resourceMetrics.dynamodb.itemCount?.toLocaleString()}</div>
                    <div><strong>Size:</strong> {resourceMetrics.dynamodb.tableSizeMB} MB</div>
                    <div><strong>Billing:</strong> {resourceMetrics.dynamodb.billingMode}</div>
                    <div><strong>Status:</strong> <span style={{ color: '#28a745' }}>{resourceMetrics.dynamodb.status}</span></div>
                  </div>
                </div>
              )}
              
              {/* S3 Card */}
              {resourceMetrics.s3 && !resourceMetrics.s3.error && (
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '16px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#FF9900' }}>
                    📦 S3 Storage
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', display: 'grid', gap: '6px' }}>
                    <div><strong>Objects:</strong> {resourceMetrics.s3.objectCount?.toLocaleString()}</div>
                    <div><strong>Size:</strong> {resourceMetrics.s3.totalSizeMB} MB ({resourceMetrics.s3.totalSizeGB} GB)</div>
                    <div><strong>Bucket:</strong> {resourceMetrics.s3.bucket}</div>
                    <div><strong>Prefix:</strong> {resourceMetrics.s3.prefix?.replace(/\/$/, '') || resourceMetrics.s3.prefix}</div>
                  </div>
                </div>
              )}
              
              {/* Lambda Card */}
              {resourceMetrics.lambda && !resourceMetrics.lambda.error && (
                <div style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '16px',
                  background: '#f8f9fa'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#FF9900' }}>
                    ⚡ Lambda
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', display: 'grid', gap: '6px' }}>
                    {resourceMetrics.lambda.functionName && (
                      <div><strong>Function:</strong> {resourceMetrics.lambda.functionName}</div>
                    )}
                    {resourceMetrics.lambda.runtime && resourceMetrics.lambda.runtime !== 'N/A' && (
                      <div><strong>Runtime:</strong> {resourceMetrics.lambda.runtime}</div>
                    )}
                    {resourceMetrics.lambda.internalBuild && (
                      <div><strong>Internal Build:</strong> <span style={{color: '#FF9900', fontWeight: 'bold'}}>{resourceMetrics.lambda.internalBuild}</span></div>
                    )}
                    <div><strong>Invocations (24h):</strong> {resourceMetrics.lambda.invocationsLast24h?.toLocaleString()}</div>
                    <div><strong>Errors (24h):</strong> {resourceMetrics.lambda.errorsLast24h}</div>
                    <div><strong>Error Rate:</strong> {resourceMetrics.lambda.errorRate}</div>
                    {resourceMetrics.lambda.memoryMB && resourceMetrics.lambda.memoryMB !== 'N/A' && (
                      <div><strong>Memory:</strong> {resourceMetrics.lambda.memoryMB} MB</div>
                    )}
                    {resourceMetrics.lambda.timeoutSeconds && resourceMetrics.lambda.timeoutSeconds !== 'N/A' && (
                      <div><strong>Timeout:</strong> {resourceMetrics.lambda.timeoutSeconds}s</div>
                    )}
                    {resourceMetrics.lambda.note && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#888', fontStyle: 'italic' }}>{resourceMetrics.lambda.note}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Active Workers */}
        {stats.workerIds && Array.isArray(stats.workerIds) && stats.workerIds.length > 0 && (
          <div className="active-workers">
            <h2>Active Workers</h2>
            <div className="worker-list">
              {stats.workerIds.map((workerId) => (
                <div key={workerId} className="worker-item">
                  {workerId}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Jobs */}
        <div className="recent-jobs">
          <h2>Recent Jobs</h2>
          {recentJobs.length === 0 ? (
            <p className="no-jobs">No recent jobs found</p>
          ) : (
            <div className="jobs-table">
              <table>
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleSort('jobId')}>
                      Job ID {getSortIndicator('jobId')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('type')}>
                      Type {getSortIndicator('type')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('status')}>
                      Status {getSortIndicator('status')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('worker')}>
                      Worker {getSortIndicator('worker')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('created')}>
                      Created {getSortIndicator('created')}
                    </th>
                    <th className="sortable" onClick={() => handleSort('completed')}>
                      Completed {getSortIndicator('completed')}
                    </th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedJobs().map((job) => (
                    <tr key={job.jobId} className={`job-row status-${job.status}`}>
                      <td className="job-id">{job.jobId}</td>
                      <td>{job.jobType}</td>
                      <td>
                        <span className={`status-badge status-${job.status}`}>
                          {job.status}
                        </span>
                      </td>
                      <td>{job.workerId || '-'}</td>
                      <td>{formatTime(job.createdAt)}</td>
                      <td>{job.status === 'completed' && job.completedAt ? formatTime(job.completedAt) : '-'}</td>
                      <td>
                        {job.status === 'completed' && job.outputS3Uri ? (
                          <button
                            className="btn btn-download"
                            onClick={() => handleDownload(job.jobId, job.jobType)}
                            disabled={downloading[job.jobId]}
                            title={`Download ${job.jobType === 'linkedin_people_search' ? 'CSV' : 'text'} result`}
                          >
                            {downloading[job.jobId] ? '⏳...' : '⬇️ Download'}
                          </button>
                        ) : (
                          <span style={{ color: '#999' }}>-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

